
import React, { useMemo, useState } from 'react';
import { Order, Driver, SystemConfig } from '../types';
import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2, Loader2, CheckSquare, Square, MessageCircle, Snowflake, Zap, Repeat2, Ghost } from 'lucide-react';
import { OrderBadges } from './order/OrderBadges';
import { OrderActions } from './order/OrderActions';
import { OrderMenu } from './order/OrderMenu';
import { CommunicationDropdown } from './order/CommunicationDropdown';
import { getOrderStatus } from '../services/math/status';
import { analyzeSafeDispatch, calculateSafeDispatchSequence } from '../services/logistics/core/timing';
import { handleSilentPrint } from '../services/silentPrinter';
import { formatRouteTag, getFleetLabel } from '../services/logistics/core/routePresentation';

const formatClientName = (name: string, isCompact: boolean) => {
    if (!name) return 'Cliente';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];

    const limit = isCompact ? 2 : 3;
    const namesToShow = parts.slice(0, limit);

    // Se temos mais de 3 nomes e não é compacto, ou temos mais de 2 e é compacto, 
    // abreviamos o último nome do conjunto que estamos mostrando
    if (parts.length > limit) {
        const lastIndex = namesToShow.length - 1;
        namesToShow[lastIndex] = namesToShow[lastIndex].charAt(0) + '.';
    }

    return namesToShow.join(' ');
};

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const SalesChannelBadge: React.FC<{ channel: string }> = ({ channel }) => {
    const name = channel.toLowerCase();
    let bgColor = 'bg-sal-cyber-teal'; // Default para canais próprios (Azul/Teal do sistema)
    
    if (name.includes('ifood')) bgColor = 'bg-red-600';
    else if (name.includes('99food')) bgColor = 'bg-yellow-400';
    else if (name.includes('shopee')) bgColor = 'bg-orange-600';
    else if (name.includes('mercado livre')) bgColor = 'bg-yellow-300';
    
    const initial = channel.charAt(0).toUpperCase();
    
    return (
        <div 
            className={`w-5 h-5 rounded-full ${bgColor} flex items-center justify-center text-white text-xs font-black shrink-0 shadow-sm`} 
            title={channel}
        >
            {initial}
        </div>
    );
};

interface Props {
    order: Order & { groupId?: string | null; groupIndex?: number; totalInGroup?: number; groupColor?: string };
    isSelected: boolean;
    isFocused?: boolean;
    onToggle: (id: string) => void;
    onFocus?: (id: string) => void;
    onDelete: (id: string) => void;
    onEdit?: (order: Order) => void;
    drivers?: Driver[];
    onAssignDriver?: (orderId: string, driverId: string) => void;
    onManualComplete?: (orderId: string) => void;
    geocodingFailed?: boolean;
    isLoading?: boolean;
    isReady?: boolean;
    onNotifyDelay?: (id: string) => void;
    onTrackOrder?: (orderId: string) => void;
    onReportIncident?: (order: Order) => void;
    systemConfig?: SystemConfig;
    isSimpleView: boolean;
    isCompact?: boolean;
    onDispatchCluster?: (orders: Order[], mode: 'own' | 'third', driverId?: string) => void;
    onConfirmOrder?: (orderId: string) => void;
    onToggleReady?: (orderId: string) => void;
    suggestion?: any;
    timeTick?: number;
}

/**
 * v45.5: OrderCard - Strategy Consistency
 * - Aplica analyzeSafeDispatch respeitando se o cérebro sugeriu Frota ou App.
 * - Garante que os badges reflitam as configurações independentes.
 */
export const OrderCard: React.FC<Props> = ({
    order, isSelected, isFocused, onToggle, onFocus, onDelete, onEdit, drivers = [],
    systemConfig, isSimpleView, isCompact = false, onDispatchCluster, onConfirmOrder, onManualComplete, onToggleReady, onNotifyDelay, onTrackOrder, onReportIncident, suggestion, timeTick
}) => {
    const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
    const isCommOpen = !!anchorRect;

    const S = getOrderStatus(order);

    const mainId = order.displayId ? `#${order.displayId}` : (order.shortCode ? `#${order.shortCode}` : `#${order.id.slice(-4)}`);
    const isMarketplace = !!order.shortCode;
    const isIfood = order.salesChannel?.toLowerCase().includes('ifood');

    // v46.1: Passive Countdown for Safe Dispatch
    // Agora o OrderCard usa o valor real de VIAGEM calculado pelo motor de análise
    // mantendo consistência total com o backend, sem duplicação de regra (2.8 etc)
    const safeAnalysis = useMemo(() => {
        const isOwn = order.driverId
            ? !drivers.find((d) => d.id === order.driverId)?.status.includes('3rd')
            : (order.optimizationOutcome !== 'third');
        const driver = drivers.find((d) => d.id === order.driverId);

        // v58.2: Se o pedido faz parte de uma rota sugerida, o cálculo deve considerar a sequência
        const suggestedRoute = suggestion?.routes?.find((r: any) => 
            r.orders?.some((ro: any) => ro.id === order.id)
        );

        if (suggestedRoute && suggestedRoute.orders.length > 1) {
            const stops = calculateSafeDispatchSequence(
                suggestedRoute.orders, 
                systemConfig || {} as any, 
                isOwn, 
                [], 
                driver
            );
            const myStop = stops.find(s => s.orderId === order.id);
            if (myStop) return myStop.analysis;
        }

        return analyzeSafeDispatch(order, systemConfig || {} as any, 0, isOwn, [], driver);
    }, [order, systemConfig, drivers, suggestion]);

    // Extrai o tempo de viagem calculado centralmente (que já considera fallback ou dados reais)
    const baseTravelTime = safeAnalysis.travel;

    const safeDispatchLimit = safeAnalysis.value;

    const isRetention = suggestion?.smartWaitAnalysis?.active && suggestion.smartWaitAnalysis.readyOrders.some((o: any) => o.id === order.id);
    const isBoomerang = suggestion?.sequentialOpportunity?.active && suggestion.sequentialOpportunity.trip1.some((o: any) => o.id === order.id);
    const isDrainage = suggestion?.metrics?.reason === 'DRAINAGE' || (suggestion?.metrics?.isSplitRecommended && order.optimizationOutcome === 'third');
    const acceptedStrategies = order.acceptedStrategies || [];
    const isRetentionConfirmed = acceptedStrategies.includes('production_retention') || acceptedStrategies.includes('dispatch_hold');
    const isBoomerangConfirmed = acceptedStrategies.includes('boomerang') || order.isBoomerangReserved === true;
    const shouldShowHeldBadge = order.isHeldForBatching === true || isRetention || isRetentionConfirmed;
    const shouldShowBoomerangBadge = isBoomerang || isBoomerangConfirmed;

    const suggestedRouteIndex = suggestion?.routes?.findIndex((route: any) =>
        route.orders?.some((routeOrder: any) => routeOrder.id === order.id)
    ) ?? -1;
    const suggestedRoute = suggestedRouteIndex >= 0 ? suggestion.routes[suggestedRouteIndex] : null;
    const routeMode: 'own' | 'third' = suggestedRoute?.type
        || (order.status === 'dispatched_3rd_party' || order.optimizationOutcome === 'third' || order.manualAssignment === 'third' ? 'third' : 'own');
    const routeBaseId = order.smartClusterId || suggestedRoute?.orders?.[0]?.smartClusterId || (suggestedRouteIndex >= 0 ? `cluster_${suggestedRouteIndex}` : undefined);
    const routeTag = routeBaseId ? formatRouteTag(routeBaseId) : null;
    const assignedDriverName = order.driverId ? drivers.find(d => d.id === order.driverId)?.name : undefined;
    const routeAssignee = assignedDriverName || suggestedRoute?.assigneeName || (routeMode === 'third' ? (order.bestProvider || 'Aplicativo parceiro') : 'Sem motoboy');
    const totalOrderValue = (order.orderValue ?? 0) + (order.deliveryFee ?? 0);

    const borderColor = S.isReady ? 'border-status-ready shadow-[0_0_10px_rgba(16,185,129,0.2)]' : (S.isPending ? 'border-status-pending' : 'border-card-border');
    const cardBg = 'bg-card-bg';

    return (
        <motion.button
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            type="button"
            className={`relative rounded-xl border-l-[4px] border-y border-r group shadow-lg transition-all text-left w-full ${isCompact ? 'mb-1.5' : 'mb-2 xl:mb-3'} ${borderColor} ${cardBg}`}
            onClick={() => { if (onEdit) onEdit(order); if (onFocus) onFocus(order.id); }}
        >
            <div className={isCompact ? 'p-1.5' : 'p-2 xl:p-3'}>
                {isCompact ? (
                    // --- COMPACT VIEW (2 Lines) ---
                    <div className="flex flex-col gap-0.5">
                        {/* Line 1: Identity & Status */}
                        <div className="flex items-center justify-between min-w-0">
                            <div className="flex items-center gap-1.5 min-w-0 flex-1 mr-2">
                                <button
                                    type="button"
                                    aria-label={isFocused ? `Desmarcar pedido ${mainId}` : `Selecionar pedido ${mainId}`}
                                    onClick={(e) => { e.stopPropagation(); onToggle(order.id); }}
                                    className={`p-0 rounded transition-all ${isFocused ? 'text-sal-cyber-teal bg-sal-cyber-teal/5' : 'text-icon-default hover:text-icon-active hover:bg-sal-gunmetal-light/5'}`}
                                >
                                    {isFocused ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                                </button>

                                <span className={`font-mono font-black text-xs px-1 py-0.5 rounded uppercase-ops ${isMarketplace ? 'bg-sal-burnt-orange/10 text-sal-burnt-orange' : 'bg-sal-cyber-teal/10 text-sal-cyber-teal'}`}>
                                    {mainId}
                                </span>

                                {isRetention && <Snowflake className="w-3 h-3 text-status-info" />}
                                {isBoomerang && <Repeat2 className="w-3 h-3 text-status-info opacity-70" />}
                                {isDrainage && <Zap className="w-3 h-3 text-status-critical" />}
                                {order.ghostDispatch && <Ghost className="w-3 h-3 text-sal-cyber-teal" />}
                                {/* v46.2: Indicador de Grupo */}
                                {order.groupId && order.totalInGroup && order.totalInGroup > 1 && (
                                  <div 
                                    className="flex items-center gap-0.5 px-1 py-0.5 rounded text-xs font-black text-white uppercase-ops"
                                    style={{ backgroundColor: order.groupColor || '#10b981' }}
                                  >
                                    <span>{order.groupIndex}</span>
                                    <span className="opacity-70">/{order.totalInGroup}</span>
                                  </div>
                                )}

                                <span className="font-bold text-text-primary truncate text-sm flex-1 tracking-tight">{formatClientName(order.customerName, true)}</span>
                            </div>

                            <div className="flex items-center gap-0.5 shrink-0">
                                <OrderBadges
                                    order={order}
                                    safeDispatchLimit={safeDispatchLimit}
                                    adjustedTravelTime={baseTravelTime}
                                    elapsedMinutes={0}
                                    isWaitingPickup={S.isWaitingPickup}
                                    isThirdParty={order.status === 'dispatched_3rd_party' || order.optimizationOutcome === 'third'}
                                    isBoomerangReserved={shouldShowBoomerangBadge}
                                    isHeld={shouldShowHeldBadge}
                                    isCritical={safeDispatchLimit <= 0}
                                    isHighRisk={safeDispatchLimit < 10}
                                    isSafe={safeDispatchLimit > 20}
                                    systemConfig={systemConfig}
                                    analysis={safeAnalysis}
                                    ultraCompact={true}
                                />
                                <OrderMenu
                                    order={order}
                                    isReturned={S.isReturned || S.isCanceled}
                                    isConfirmingDelete={false}
                                    onEdit={onEdit}
                                    onReportIncident={onReportIncident}
                                    onDeleteClick={(e) => { e.stopPropagation(); onDelete(order.id); }}
                                    onManualComplete={onManualComplete}
                                    ultraCompact={true}
                                />
                            </div>
                        </div>

                        {/* Line 2: Address & Actions */}
                        <div className="flex items-center justify-between min-w-0 pt-0.5 border-t border-card-border mt-0.5">
                            <div className="flex items-center gap-1.5 min-w-0 flex-1 mr-2 overflow-hidden">
                                {/* Valor do Pedido */}
                                <span className="text-xs font-black text-text-primary whitespace-nowrap uppercase-ops">
                                    {formatCurrency(totalOrderValue)}
                                </span>

                                {/* Canal de Venda */}
                                <SalesChannelBadge channel={order.salesChannel || 'Próprio'} />

                                {/* ID da Frota / Rota */}
                                {routeTag && (
                                    <div className="flex items-center gap-0.5 shrink-0">
                                        <span className={`text-[10px] h-4 flex items-center font-black px-1 rounded uppercase-ops ${routeMode === 'third' ? 'bg-sal-burnt-orange/10 text-sal-burnt-orange' : 'bg-sal-solid-indigo/10 text-sal-solid-indigo'}`}>
                                            {routeTag}
                                        </span>
                                    </div>
                                )}

                                <div className="text-[10px] font-medium text-text-muted truncate opacity-90 flex-1 min-w-0">
                                    {order.isLoading ? (
                                        <span className="text-sal-cyber-teal uppercase-ops">Triagem…</span>
                                    ) : (
                                        <>
                                            {order.neighborhood} - {order.streetAddress}{order.addressNumber ? `, ${order.addressNumber}` : ''}
                                            {(routeAssignee || routeMode === 'third') && (
                                                <span className="ml-1 opacity-70 uppercase-ops">
                                                    • {routeMode === 'third' 
                                                        ? `Parceiro Logístico ${order.groupIndex || '?'}/${order.totalInGroup || '?'}` 
                                                        : routeAssignee}
                                                </span>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-0.5 shrink-0">
                                <OrderActions
                                    order={order}
                                    systemConfig={systemConfig}
                                    isAccepting={order.isLoading}
                                    isConfirmingHeldReady={false}
                                    onConfirmClick={(e) => { e.stopPropagation(); onConfirmOrder?.(order.id); }}
                                    onReadyToggle={(e) => { e.stopPropagation(); onToggleReady?.(order.id); }}
                                    onTrackOrder={onTrackOrder}
                                    onManualComplete={onManualComplete}
                                    onPrint={(e) => { e.stopPropagation(); handleSilentPrint(order, systemConfig); }}
                                    canPrint={true}
                                    onOpenCommunication={(o, rect) => setAnchorRect(rect)}
                                    compact={true}
                                    isSimpleView={isSimpleView}
                                    ultraCompact={true}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    // --- STANDARD VIEW (Original) ---
                    <>
                        <div className="flex justify-between items-start mb-1.5">
                            <div className="flex items-start gap-2.5 min-w-0 flex-1">
                                <div className="flex flex-col items-center gap-2 shrink-0">
                                    <button
                                        type="button"
                                        aria-label={isFocused ? `Desmarcar pedido ${mainId}` : `Selecionar pedido ${mainId}`}
                                        onClick={(e) => { e.stopPropagation(); onToggle(order.id); }}
                                        className={`p-0 rounded transition-all ${isFocused ? 'text-sal-cyber-teal bg-sal-cyber-teal/5' : 'text-icon-default hover:text-icon-active hover:bg-sal-gunmetal-light/5'}`}
                                    >
                                        {isFocused ? <CheckSquare className="w-4.5 h-4.5" /> : <Square className="w-4.5 h-4.5" />}
                                    </button>
                                    {!order.isLoading && <SalesChannelBadge channel={order.salesChannel || 'Próprio'} />}
                                </div>
                                <div className="flex flex-col min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5 xl:gap-2 mb-0.5 xl:mb-1">
                                        <span className={`font-mono font-black text-xs px-1 py-0.5 rounded uppercase-ops ${isMarketplace ? 'bg-sal-burnt-orange/10 text-sal-burnt-orange' : 'bg-sal-cyber-teal/10 text-sal-cyber-teal'}`}>
                                            {mainId}
                                        </span>

                                        {/* v46.1: Strategy Icons (Mirrored from Compact View) */}
                                        {isRetention && <Snowflake className="w-3.5 h-3.5 text-status-info" />}
                                        {isBoomerang && <Repeat2 className="w-3.5 h-3.5 text-status-info opacity-70" />}
                                        {isDrainage && <Zap className="w-3.5 h-3.5 text-status-critical" />}
                                        {order.ghostDispatch && <Ghost className="w-3.5 h-3.5 text-sal-cyber-teal" />}

                                        <span className={`font-bold text-text-primary truncate tracking-tight ${isCompact ? 'text-sm' : 'text-base'}`}>{formatClientName(order.customerName, isCompact || isSimpleView)}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 min-w-0 mt-0.5">
                                        <div className="text-xs xl:text-xs font-medium text-text-muted opacity-90 truncate flex-1 min-w-0">
                                            {order.isLoading ? (
                                                <span className="text-sal-cyber-teal uppercase-ops">Triagem…</span>
                                            ) : (
                                                <>
                                                    <span className="font-black text-text-primary">{order.neighborhood}</span>
                                                    <span className="mx-1 opacity-50">-</span>
                                                    <span>{order.streetAddress}, {order.addressNumber || 'S/N'}{order.complement ? ` - ${order.complement}` : ''}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="shrink-0 flex items-start gap-0.5">
                                <OrderBadges
                                    order={order}
                                    safeDispatchLimit={safeDispatchLimit}
                                    adjustedTravelTime={baseTravelTime}
                                    elapsedMinutes={0}
                                    isWaitingPickup={S.isWaitingPickup}
                                    isThirdParty={order.status === 'dispatched_3rd_party' || order.optimizationOutcome === 'third'}
                                    isBoomerangReserved={shouldShowBoomerangBadge}
                                    isHeld={shouldShowHeldBadge}
                                    isCritical={safeDispatchLimit <= 0}
                                    isHighRisk={safeDispatchLimit < 10}
                                    isSafe={safeDispatchLimit > 20}
                                    systemConfig={systemConfig}
                                    analysis={safeAnalysis}
                                    ultraCompact={true}
                                />
                                <OrderMenu
                                    order={order}
                                    isReturned={S.isReturned || S.isCanceled}
                                    isConfirmingDelete={false}
                                    onEdit={onEdit}
                                    onReportIncident={onReportIncident}
                                    onDeleteClick={(e) => { e.stopPropagation(); onDelete(order.id); }}
                                    onManualComplete={onManualComplete}
                                    ultraCompact={true}
                                />
                            </div>
                        </div>

                        <div className={`flex items-center justify-between border-t border-card-border ${isCompact ? 'pt-1.5 mt-1' : 'pt-2 mt-1'} gap-2`}>
                            <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
                                {/* Valor do Pedido */}
                                <span className="text-xs font-black text-text-primary whitespace-nowrap uppercase-ops shrink-0">
                                    {formatCurrency(totalOrderValue)}
                                </span>

                                {/* ID da Frota / Rota */}
                                {routeTag && (
                                    <div className="flex items-center gap-0.5 shrink-0">
                                        <span className={`text-xs h-5 flex items-center font-black px-1.5 rounded uppercase-ops ${routeMode === 'third' ? 'bg-sal-burnt-orange/10 text-sal-burnt-orange' : 'bg-sal-solid-indigo/10 text-sal-solid-indigo'}`}>
                                            {routeTag}
                                        </span>
                                    </div>
                                )}

                                {/* Entregador / Parceiro */}
                                {(routeAssignee || routeMode === 'third') && (
                                    <span className="text-xs font-bold text-text-muted uppercase-ops opacity-80 truncate flex-1">
                                        • {routeMode === 'third' 
                                            ? `Parceiro Logístico ${order.groupIndex || '?'}/${order.totalInGroup || '?'}` 
                                            : routeAssignee}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-1 ml-auto shrink-0">
                                <OrderActions
                                    order={order}
                                    systemConfig={systemConfig}
                                    isAccepting={order.isLoading}
                                    isConfirmingHeldReady={false}
                                    onConfirmClick={(e) => { e.stopPropagation(); onConfirmOrder?.(order.id); }}
                                    onReadyToggle={(e) => { e.stopPropagation(); onToggleReady?.(order.id); }}
                                    onTrackOrder={onTrackOrder}
                                    onManualComplete={onManualComplete}
                                    onPrint={(e) => { e.stopPropagation(); handleSilentPrint(order, systemConfig); }}
                                    canPrint={true}
                                    onOpenCommunication={(o, rect) => setAnchorRect(rect)}
                                    compact={true}
                                    isSimpleView={isSimpleView}
                                    ultraCompact={true}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>

            <CommunicationDropdown
                isOpen={isCommOpen}
                onClose={() => setAnchorRect(null)}
                order={order}
                systemConfig={systemConfig}
                driver={drivers.find(d => d.id === order.driverId)}
                anchorRect={anchorRect}
            />
        </motion.button>
    );
};
